import { v4 } from 'uuid';
import _ from 'lodash';

import Emitter, { EVENT_KEY } from './eventEmitter';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import apiService from 'src/services/api-service';

const APP_EVENT_TRACKER_URL = process.env.REACT_APP_APP_EVENT_TRACKER_URL;
// Schema version = 0 is for non-production environment
const APP_EVENT_SCHEMA_VERSION = Number(process.env.REACT_APP_APP_EVENT_SCHEMA_VERSION || 0);
const BASE_URL = process.env.REACT_APP_FILTER_ADMIN_HTML_BASE_URL;

const getBoostPlan = tenantApp => {
  return _.get(tenantApp, 'selectedPlan', '').replace('_discount', '') || 'Trial';
};

/**
 * Event tracker
 */
class EventTracker {
  constructor() {
    this.tenantApp = null;
    this.eventListenersAdded = false;
    this.eventHandlersAdded = false;
  }

  /**
   * Set user
   * @param {object} tenantApp
   */
  setTenantApp(tenantApp) {
    if (!APP_EVENT_TRACKER_URL) return;
    if (_.isEmpty(tenantApp)) {
      console.log('EventTracker.setTenantApp() - tenantApp not detected');
      return;
    }

    if (this.tenantApp) {
      console.log('EventTracker.setTenantApp() - already set', this.tenantApp);
      return;
    }

    const plan = getBoostPlan(tenantApp);
    const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
    this.tenantApp = {
      external_id: tenantApp.externalInfo?.id,
      display_name: tenantApp.externalInfo?.name,
      tenant_id: tenantApp.tenantId,
      email: tenantApp.externalInfo?.email,
      boost_plan: plan,
      shopify_plan: _.get(tenantApp, 'externalInfo.plan_display_name', ''),
      product_count: tenantApp.appParams?.currentTotalProducts,
      name: tenantApp.externalInfo?.name,
      language: tenantApp.appLanguage,
      country: tenantApp.externalInfo?.country_name,
      dev_store: _.startsWith(tenantApp.tenantId, 'bc-dev'),
      ssr_enable: isEnableShopifyIntegration,
      subscription_v4_enable: _.get(tenantApp, 'settings.subscriptionV4.settings.enable', false),
      exceeded_plan: _.get(tenantApp, 'exceededPlan', false)
    };

    // Check active tenant
    if (plan === 'Trial') {
      const trialExpiresAt = _.get(tenantApp, 'trialExpiresAt', 0);
      this.tenantApp.trial_expires_at = new Date(trialExpiresAt * 1000).toISOString();
      this.tenantApp.trial_expired = _.get(tenantApp, 'trialExpired', false);
    }
    console.log('EventTracker.setTenantApp()', this.tenantApp);
  }

  /**
   * Add event listeners
   */
  addEventListeners() {
    if (!APP_EVENT_TRACKER_URL) {
      console.log('EventTracker.addEventListeners() - Disabled');
      return;
    }

    if (this.eventListenersAdded) {
      console.log('EventTracker.addEventListeners() - Already added');
      return;
    }

    document.addEventListener('click', this.handleGlobalClick);
    this.eventListenersAdded = true;
  }

  /**
   * Check if element is a menu element
   * @param {HTMLElement} element
   * @returns {HTMLElement}
   */
  // getMenuElement(element) {
  //   return element.closest('[id^="menu-"]');
  // }

  /**
   * Handle global click
   * @param {Event} event
   */
  handleGlobalClick = event => {
    const target = event.target;
    const closestButtonOrLink = target.closest('button') || target.closest('a');
    // const menuItem = this.getMenuElement(target);
    if (target.tagName === 'A' || target.tagName === 'BUTTON' || target.tagName === 'INPUT') {
      this.trackClick(target);
    } else if (closestButtonOrLink) {
      this.trackClick(closestButtonOrLink);
    }
    //  else if (menuItem) {
    //   this.trackClick(menuItem);
    // }
  };

  /**
   * Track click
   * @param {HTMLElement} element
   */
  trackClick(element) {
    if (!APP_EVENT_TRACKER_URL) return;

    const payload = {
      tenant_app: this.tenantApp,
      element: {
        type: element.tagName.toLowerCase(),
        name: this.parseElementName(element),
        value: this.parseElementValue(element),
        selected: this.isElementSelected(element)
      }
    };
    const eventData = {
      id: v4(),
      name: payload.element.name,
      session_id: apiService.getSessionId(),
      type: 'frontend',
      source: 'admin-html-v2',
      tenant_id: this.tenantApp?.tenant_id,
      payload: this.flattenPayload(payload),
      timestamp: new Date().toISOString(),
      schema_version: APP_EVENT_SCHEMA_VERSION
    };
    if (eventData.name) {
      Emitter.emit(EVENT_KEY.TRACK_USER_CLICK, eventData);
    }
  }

  /**
   * Track page view
   * @param {string} pathName
   * @param {string} pageReferrerPath
   */
  trackPageView(pagePath, pageReferrerPath) {
    if (!APP_EVENT_TRACKER_URL || !pagePath) return;

    const pageTitle = _.capitalize(pagePath.split('/').pop().replace(/[-_]/g, ' '));
    const event = {
      id: v4(),
      name: `Page View:${pageTitle}`,
      session_id: apiService.getSessionId(),
      type: 'frontend',
      source: 'admin-html-v2',
      tenant_id: this.tenantApp?.tenant_id,
      payload: this.flattenPayload({
        page_title: pageTitle,
        page_location: `${BASE_URL}${pagePath}`,
        page_referrer: `${BASE_URL}${pageReferrerPath}`,
        tenant_app: this.tenantApp
      }),
      timestamp: new Date().toISOString(),
      schema_version: APP_EVENT_SCHEMA_VERSION
    };
    this.trackEvent(event);
  }

  /**
   * Track event after user successfully started free trial during onboarding
   * @param {*} eventProps
   * @returns
   */
  wrapAndTrackEvent(eventName, eventProps) {
    if (!APP_EVENT_TRACKER_URL) return;

    try {
      const event = {
        id: v4(),
        name: eventName,
        session_id: apiService.getSessionId(),
        type: 'frontend',
        source: 'admin-html-v2',
        tenant_id: this.tenantApp?.tenant_id,
        payload: this.flattenPayload({
          ...eventProps,
          tenant_app: this.tenantApp
        }),
        timestamp: new Date().toISOString(),
        schema_version: APP_EVENT_SCHEMA_VERSION
      };
      this.trackEvent(event);
    } catch (error) {
      console.error('EventTracker.wrapAndTrackEvent() - Error:', error);
      return;
    }
  }

  /**
   * Parse element value
   * @param {HTMLElement} element
   * @returns {string}
   */
  parseElementValue(element) {
    if (element.tagName === 'INPUT') {
      return element.value;
    }
    return '';
  }

  /**
   * Parse element name. If format changed => need to update the event handler below
   * - boost-pfs-backend/packages/app-event-tracker/src/vitally/vitally.event-parser.ts
   *
   * @param {HTMLElement} element
   * @returns {string}
   */
  parseElementName(element) {
    let name = element.getAttribute('aria-label') || element.alt;
    if (name) return name;

    if (element.tagName === 'INPUT') {
      // <input id="feature.filterCollection" type="checkbox" class="Polaris-Checkbox__Input" aria-invalid="false" role="checkbox" aria-checked="true" value="" checked="">
      // ==> checkbox::feature.filterCollection
      // <input id="style3" name="suggestionStyle" type="radio" class="Polaris-RadioButton__Input" value="">
      // ==> radio::suggestionStyle:style3
      return `${element.type}:${element.name}:${element.id}`;
    }
    return name;
  }

  /**
   * Get element selected state
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  isElementSelected(element) {
    if (element.tagName === 'INPUT' && ['checkbox', 'radio'].includes(element.type)) {
      return element.checked;
    }
    return false;
  }

  /**
   * Parse event payload
   * @param {Object} payload
   * @returns {string} Ex: tenant_app.externalId:12345|tenant_app.displayName:MyStore|element.type:button|element.value:Submit
   */
  flattenPayload(payload) {
    const flattenObject = (obj, prefix = '') => {
      return Object.keys(obj).reduce((acc, k) => {
        const pre = prefix.length ? prefix + '.' : '';
        if (typeof obj[k] === 'object' && obj[k] !== null && !Array.isArray(obj[k])) {
          Object.assign(acc, flattenObject(obj[k], pre + k));
        } else {
          acc[pre + k] = obj[k];
        }
        return acc;
      }, {});
    };

    const flattenedPayload = flattenObject(payload);
    return Object.entries(flattenedPayload)
      .filter(([, value]) => value != null)
      .map(([key, value]) => `${key}:${value}`)
      .join('|');
  }

  /**
   * Handle events
   */
  handleEvents() {
    if (!APP_EVENT_TRACKER_URL) {
      // console.log('EventTracker.handleEvents() - Disabled');
      return;
    }

    if (this.eventHandlersAdded) {
      // console.log('EventTracker.handleEvents() - Already added');
      return;
    }

    Emitter.on(EVENT_KEY.TRACK_USER_CLICK, this.handleUserClick);
    this.eventHandlersAdded = true;
  }

  /**
   * Handle user click
   * @param {object} eventData
   */
  handleUserClick = eventData => {
    // Here you can send the event data to your analytics service
    // Example: send to an analytics API
    this.trackEvent(eventData);
  };

  /**
   * Track event
   * @param {object} eventData
   */
  trackEvent(eventData) {
    if (!APP_EVENT_TRACKER_URL) return;

    // console.log('EventTracker.trackEvent()', _.pick(eventData, ['name', 'tenant_id']));
    fetch(APP_EVENT_TRACKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData)
    })
      .then(response => response.json())
      .catch(error => console.error('EventTracker.trackEvent() - Error:', error));
  }
}

const eventTracker = new EventTracker();
export default eventTracker;
